import { useCallback, useEffect, useMemo, useState } from "react";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { learnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import {
  LearnFlashcardStage,
  type LearnFlashcardStageProps,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck._components.card";
import {
  type LearnCardControls,
  LearnDeckContext,
  useRegisterLearnCardControls,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { SlideLessonPageContent } from "#slide-lesson-page-content";
import {
  SlideLessonPageContext,
  SlideLessonPageContextProvider,
  useGoToSlidePage,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._context";
import { SlideFavoriteButton } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-favorite-button";
import { SlideTextSizeButton } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-text-size-button";

function SlideFlashcardControls({ presentationKey }: { presentationKey: string }) {
  const [value] = useContextSelectors(SlideLessonPageContext, (c) => c);
  const [slideViewMemory] = useContextSelectors(LearnDeckContext, (c) => c.slideViewMemory);
  const { currentPage, pageCount, setCurrentPage, views } = value;
  const goToPage = useGoToSlidePage();
  // Undo from the card after this one returns here, so the view the learner is on outlives the card.
  useEffect(() => {
    slideViewMemory.set(presentationKey, currentPage);
  }, [currentPage, presentationKey, slideViewMemory]);
  const pages = useMemo(
    () =>
      pageCount > 1
        ? {
            count: pageCount,
            current: currentPage,
            goTo: goToPage,
            onSwipeToPage: setCurrentPage,
            views,
          }
        : null,
    [currentPage, goToPage, pageCount, setCurrentPage, views],
  );
  const controls = useMemo<LearnCardControls>(
    () => ({
      actions: (
        <SlideLessonPageContext.Provider value={value}>
          <SlideTextSizeButton />
          <SlideFavoriteButton />
        </SlideLessonPageContext.Provider>
      ),
      getActionSnapshot: async () => ({ kind: "slide" as const, slideSlug: value.slideSlug }),
      hasCustomFooter: value.slideFooterContent !== null,
      kind: "slide",
      nextButtonLabel: value.nextButtonLabel,
      pages,
    }),
    [pages, value],
  );
  useRegisterLearnCardControls(controls);
  return null;
}

/**
 * Authored slides use the same fixed body, prepared underlay and UI-thread swipe as study cards. A
 * slide longer than one page becomes a slideshow (see `SlideLessonPageContent`) and lends the
 * deck its slides: swiping left, Next and Redo advance; swiping right and Undo return to the
 * previous slide, and nothing moves for a rightward swipe on the first. Only a forward swipe on the
 * last slide or Next Card leaves the card: it pages on, untilted, as the next flashcard follows the
 * finger in from the right edge.
 * Skip (in the button's menu) dismisses it from any view. Undo from the card after it reopens the
 * slide on the view the learner left it on.
 */
export function SlideFlashcard(
  props: Omit<LearnFlashcardStageProps, "back" | "front" | "info"> & {
    slot: LearnFlashcardStageProps["slot"] & { item: { kind: "slide" } };
  },
) {
  const [commit, slideViewMemory] = useContextSelectors(
    LearnDeckContext,
    (c) => c.commit,
    (c) => c.slideViewMemory,
  );
  const onContinue = useCallback(() => commit({ kind: "continue" }, "button"), [commit]);
  const isCurrent = props.role === "current";
  const isActive = isCurrent && (props.isActive ?? true);
  const presentationKey = learnCardPresentationKey(props.slot.presentation);
  // Read once: a remounted card (History restored this presentation) opens where it was left.
  const [initialPage] = useState(() => slideViewMemory.get(presentationKey) ?? 0);
  return (
    <SlideLessonPageContextProvider
      initialPage={initialPage}
      isActive={isActive}
      onContinue={onContinue}
      slideSlug={props.slot.item.slideSlug}
    >
      {isCurrent ? <SlideFlashcardControls presentationKey={presentationKey} /> : null}
      <LearnFlashcardStage {...props} back={null} front={<SlideLessonPageContent />} info={null} />
    </SlideLessonPageContextProvider>
  );
}
